/* Known For: compatibility fixes for Printify's Shopify personalization block. */
(() => {
  'use strict';

  if (window.knownForPersonalizationFixLoaded) return;
  window.knownForPersonalizationFixLoaded = true;

  // Printify can only draw a preview on a "mockup" (one photo angle of the shirt)
  // that shows the personalized text. Our names are printed on the BACK, but the
  // link Printify sends after "Save design" asks for the FRONT mockup, so its
  // server answers 404 "Mockup ... not found on this product".
  // These are the back mockups of our Comfort Colors 1717 tees, in the order we
  // try them: 98446 = "Back", 112434 = "Person 1 Back".
  const BACK_MOCKUP_IDS = ['98446', '112434'];
  const LOAD_TIMEOUT_MS = 8000; // stop waiting for one picture after 8 seconds

  const contexts = new WeakMap();
  const patched = new WeakSet();
  const propertyName = 'properties[Personalization ID]';
  const imageAttributes = ['src', 'srcset', 'sizes', 'alt', 'data-src'];
  const shared = () => window.personalizationSharedState;

  // Builds the list of preview addresses to try, best first:
  // the address Printify sent, then the same address with each back mockup.
  // Gives back an empty list when the address is missing or not https.
  function previewCandidates(imageUrl) {
    let sent;
    try {
      sent = new URL(imageUrl);
    } catch {
      return [];
    }
    if (sent.protocol !== 'https:') return [];

    const candidates = [sent.href];
    const mockupPart = /\/preview\/\d+$/; // for example "/preview/98445"
    if (mockupPart.test(sent.pathname)) {
      BACK_MOCKUP_IDS.forEach(id => {
        const candidate = new URL(sent.href);
        candidate.pathname = sent.pathname.replace(mockupPart, `/preview/${id}`);
        if (!candidates.includes(candidate.href)) candidates.push(candidate.href);
      });
    }
    return candidates;
  }

  // Tries to load one picture.
  // Gives back true if it loaded, false if it failed or took too long.
  function imageLoads(url) {
    return new Promise(resolve => {
      const image = new Image();
      const timer = setTimeout(() => finish(false), LOAD_TIMEOUT_MS);
      function finish(ok) {
        clearTimeout(timer);
        image.onload = null;
        image.onerror = null;
        resolve(ok);
      }
      image.onload = () => finish(image.naturalWidth > 0);
      image.onerror = () => finish(false);
      image.src = url;
    });
  }

  // Printify sends the preview picture as a file download ("octet-stream"), so a
  // link straight to it downloads a file instead of showing it. So we build a tiny
  // web page that shows the picture, keep it in the browser's memory (a "blob"),
  // and give back an address for that page.
  function makeViewerPage(pictureUrl) {
    const page = document.implementation.createHTMLDocument('Your design preview');
    const viewport = page.createElement('meta');
    viewport.name = 'viewport';
    viewport.content = 'width=device-width, initial-scale=1';
    page.head.append(viewport);
    page.body.style.cssText = 'margin:0;min-height:100vh;display:grid;place-items:center;background:#fbf4e8';
    const picture = page.createElement('img');
    picture.src = pictureUrl;
    picture.alt = 'Your personalized design preview';
    picture.style.cssText = 'max-width:100%;max-height:100vh';
    page.body.append(picture);
    const html = '<!doctype html>' + page.documentElement.outerHTML;
    return URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  }

  function createContext(root, container) {
    const originals = new Map();
    let generation = 0;
    let lastPreview = null; // the saved design we are showing, or trying to show
    let loadedPreview = null; // set only after its picture really loaded
    let viewerPageUrl = null; // the little page the "Open preview" link points to

    const status = document.createElement('div');
    status.className = 'kf-personalization-status';
    status.hidden = true;
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button--secondary';
    retry.textContent = 'Retry preview';
    retry.hidden = true;
    const openPreview = document.createElement('a');
    openPreview.className = 'link underlined-link kf-personalization-preview-link';
    openPreview.textContent = 'Open preview image in a new tab';
    openPreview.target = '_blank';
    openPreview.rel = 'noopener noreferrer';
    openPreview.hidden = true;
    status.append(message, retry, openPreview);
    container.append(status);

    function show(text, canRetry = false) {
      message.textContent = text;
      retry.hidden = !canRetry;
      // Only offer the link when we have a picture that really loads,
      // so shoppers never land on Printify's error page.
      if (viewerPageUrl) URL.revokeObjectURL(viewerPageUrl); // free the old page
      viewerPageUrl = loadedPreview ? makeViewerPage(loadedPreview.url) : null;
      openPreview.hidden = !viewerPageUrl;
      if (viewerPageUrl) openPreview.href = viewerPageUrl;
      else openPreview.removeAttribute('href');
      status.hidden = !text;
    }

    function restoreImages() {
      originals.forEach((attributes, img) => {
        if (!img.isConnected) return;
        attributes.forEach((value, name) => {
          if (value === null) img.removeAttribute(name);
          else img.setAttribute(name, value);
        });
      });
      originals.clear();
    }

    function current(preview) {
      return root.isConnected &&
        shared()?.personalizationData?.personalizationId === preview.id &&
        String(shared()?.currentVariant?.id || '') === preview.variant;
    }

    function applyImages(preview, activate = true) {
      if (!current(preview)) return false;
      const gallery = root.querySelector('media-gallery');
      if (!gallery) return false;
      const mediaId = shared()?.currentVariant?.featured_media?.id;
      const slides = [...gallery.querySelectorAll('.product__media-item[data-media-id]')];
      const slide = slides.find(item => mediaId && item.dataset.mediaId.endsWith(`-${mediaId}`)) ||
        slides.find(item => item.classList.contains('is-active')) || slides[0];
      if (!slide) return false;
      const main = slide.querySelector('.product__media img');
      if (!main) return false;
      const selectedId = slide.dataset.mediaId;
      const thumbnail = [...gallery.querySelectorAll('[data-target]')]
        .find(item => item.dataset.target === selectedId)?.querySelector('img');
      const numericId = selectedId.slice(selectedId.lastIndexOf('-') + 1);
      const modal = document.getElementById(`ProductModal-${root.dataset.section}`);
      const zoom = [...(modal?.querySelectorAll('img[data-media-id]') || [])]
        .find(img => img.dataset.mediaId === numericId);

      [main, thumbnail, zoom].filter(Boolean).forEach(img => {
        if (!originals.has(img)) {
          originals.set(img, new Map(imageAttributes.map(name => [name, img.getAttribute(name)])));
        }
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.setAttribute('src', preview.url);
        img.setAttribute('alt', 'Your personalized design preview');
        if (img.hasAttribute('data-src')) img.setAttribute('data-src', preview.url);
      });
      // Dawn keeps a separate image in its zoom dialog and its thumbnail strip.
      if (activate && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(selectedId, false);
      }
      return true;
    }

    function restore() {
      generation++; // a picture that is still loading will now be ignored
      lastPreview = null;
      loadedPreview = null;
      restoreImages();
      show('');
    }

    async function render(imageUrl) {
      const request = ++generation;
      restoreImages();
      loadedPreview = null;
      lastPreview = null;
      const data = shared()?.personalizationData;
      if (!data?.personalizationId) return show('');

      const candidates = previewCandidates(imageUrl);
      if (!candidates.length) {
        return show('Design saved. Product photo shown because no preview is available.');
      }

      const preview = {
        id: data.personalizationId,
        variant: String(shared()?.currentVariant?.id || ''),
        sentUrl: candidates[0], // the address Printify sent ("Retry preview" starts here again)
        url: null, // the address that really loads, filled in below
      };
      lastPreview = preview;
      show('Design saved. Loading your preview.');

      for (const url of candidates) {
        const loaded = await imageLoads(url);
        // While we waited, the shopper may have changed size or color, removed
        // the design or saved a new one. Then this answer is out of date.
        if (request !== generation || !current(preview)) return;
        if (!loaded) continue; // try the next address

        preview.url = url;
        loadedPreview = preview;
        if (applyImages(preview)) show('Design saved. Your personalized preview is shown.');
        else show('Design saved. The preview could not be displayed. Please retry.', true);
        return;
      }

      show('Design saved. The preview picture did not load, so the product photo is shown. ' +
        'Your design is still attached to this item.', true);
    }

    retry.addEventListener('click', () => {
      if (lastPreview && current(lastPreview)) render(lastPreview.sentUrl);
    });

    const context = { render, restore };
    // Dawn replaces the zoom contents after a variant fetch. Update that new node too.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!root.isConnected) return unsubscribe();
        if (data?.sectionId === root.dataset.section && loadedPreview && current(loadedPreview)) {
          applyImages(loadedPreview, false);
        }
      });
    }
    return context;
  }

  function install() {
    document.querySelectorAll('.personalize-container[data-block-id]').forEach(container => {
      const instance = window[`personalize_${container.dataset.blockId}`];
      const root = container.closest('product-info');
      if (!instance || !root || patched.has(instance) ||
          typeof instance.addPersonalizationToForm !== 'function' ||
          typeof instance.handleSharedStateChange !== 'function') return;
      patched.add(instance);
      let context = contexts.get(root);
      if (!context) {
        context = createContext(root, container);
        contexts.set(root, context);
      }

      instance.addPersonalizationToForm = data => {
        if (typeof data?.personalizationId !== 'string' || !data.personalizationId.trim()) return;
        root.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
          // The app creates capitalized names but removes lowercase ones. HTML
          // attribute matching is case-sensitive: remove either spelling first.
          form.querySelectorAll('input[name]').forEach(input => {
            if (input.name.toLowerCase() === propertyName.toLowerCase()) input.remove();
          });
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = propertyName;
          input.value = data.personalizationId;
          form.append(input);
        });
        if (window.Shopify?.storefrontApi) window.personalizationData = data;
      };

      instance.replaceProductImage = context.render;
      instance.restoreProductImage = context.restore;
      const originalStatus = instance.handleSharedStateChange;
      instance.handleSharedStateChange = function (event, data) {
        originalStatus.call(this, event, data);
        if ((event === 'personalizationApplied' || (event === 'variantChanged' && data.hasPersonalization)) &&
            !shared()?.personalizationData?.imageUrl) context.render(null);
      };
      // Also normalize an already-applied design if the script loads late.
      if (shared()?.personalizationData) {
        instance.addPersonalizationToForm(shared().personalizationData);
        context.render(shared().personalizationData.imageUrl);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install);
  else install();
  document.addEventListener('shopify:section:load', install);
})();
